import React, { useEffect, useState } from 'react';
import {
  FileText, Upload, AlertCircle, CheckCircle, Clock, XCircle,
  Loader2, RefreshCw, ExternalLink, Copy
} from 'lucide-react';
import { apiClient } from '../lib/api';
import { PrescriptionTransferReview } from '../components/transfers/PrescriptionTransferReview';

/**
 * The pharmacy transfer inbox: prescriptions written by outside doctors that a
 * pharmacy account sent us to fill. Each one is parsed on arrival, corrected by
 * a pharmacist, and then turned into an order.
 */

export interface PrescriptionTransfer {
  _id: string;
  status: 'uploaded' | 'parsed' | 'needs_review' | 'ready' | 'ordered' | 'rejected';
  source: 'pdf' | 'manual';
  documentFileName?: string;
  documentS3Key?: string;
  raw?: { parseWarnings?: string[]; extractor?: string };
  parsed: {
    transmission?: { messageId?: string; sentAt?: string; intendedForPharmacy?: string };
    prescriber?: {
      firstName?: string; middleName?: string; lastName?: string; credential?: string;
      npiNumber?: string; dea?: string; stateLicense?: string; licenseState?: string;
      phone?: string; fax?: string;
      address?: { street?: string; city?: string; state?: string; zipCode?: string };
    };
    patient?: {
      firstName?: string; lastName?: string; dateOfBirth?: string; gender?: string;
      phone?: string; email?: string;
      address?: { street?: string; city?: string; state?: string; zipCode?: string };
    };
    medication?: {
      description?: string; ndc?: string; quantity?: number; quantityUnit?: string;
      daysSupply?: number; sig?: string; refills?: number; daw?: string;
      writtenDate?: string; diagnosisCode?: string; diagnosisDescription?: string;
    };
    observations?: Array<{ name?: string; value?: string; unit?: string; observedAt?: string }>;
  };
  notes?: string;
  matchedProductId?: string;
  matchConfidence?: number;
  matchReason?: string;
  patientId?: string;
  orderId?: string;
  duplicateOfId?: string;
  rejectedReason?: string;
  createdAt: string;
}

const STATUS_STYLES: Record<PrescriptionTransfer['status'], { label: string; cls: string; Icon: React.ComponentType<{ className?: string }> }> = {
  uploaded: { label: 'Uploaded', cls: 'bg-gray-100 text-gray-700', Icon: Clock },
  parsed: { label: 'Parsed', cls: 'bg-blue-100 text-blue-700', Icon: CheckCircle },
  needs_review: { label: 'Needs review', cls: 'bg-amber-100 text-amber-800', Icon: AlertCircle },
  ready: { label: 'Ready to order', cls: 'bg-green-100 text-green-700', Icon: CheckCircle },
  ordered: { label: 'Ordered', cls: 'bg-indigo-100 text-indigo-700', Icon: CheckCircle },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700', Icon: XCircle }
};

export const TransferStatusBadge: React.FC<{ status: PrescriptionTransfer['status'] }> = ({ status }) => {
  const { label, cls, Icon } = STATUS_STYLES[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
};

function formatDate(value?: string): string {
  if (!value) return '—';
  const date = new Date(value);
  return isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

// The most /prescription-transfers returns; it has no paging or total, so a
// full answer means there may be older transfers we cannot show.
const TRANSFERS_LIMIT = 200;

export const PrescriptionTransfers: React.FC = () => {
  const [transfers, setTransfers] = useState<PrescriptionTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'open' | 'all'>('open');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const res = await apiClient.getPrescriptionTransfers(filter, TRANSFERS_LIMIT);
    if (res.success && res.data) {
      setTransfers(res.data);
      setError('');
    } else {
      setError(res.error || 'Failed to load transfers.');
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setError('');
    const res = await apiClient.uploadPrescriptionTransfer(file);
    setUploading(false);
    if (!res.success || !res.data) {
      setError(res.error || 'Upload failed.');
      return;
    }
    await load();
    // Drop the pharmacist straight into review — the parse always needs eyes.
    setSelectedId(res.data.transfer._id);
  };

  const startManual = async () => {
    setError('');
    const res = await apiClient.createManualPrescriptionTransfer({});
    if (!res.success || !res.data) {
      setError(res.error || 'Could not start a manual entry.');
      return;
    }
    await load();
    setSelectedId(res.data.transfer._id);
  };

  if (selectedId) {
    return (
      <PrescriptionTransferReview
        transferId={selectedId}
        onClose={() => { setSelectedId(null); load(); }}
      />
    );
  }

  const needsReviewCount = transfers.filter(t => t.status === 'needs_review').length;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transferred Prescriptions</h1>
          <p className="text-gray-600 mt-1">
            Scripts your doctors sent over, ready to be filled. Upload the prescription and we'll read
            what we can — you confirm the details before it becomes an order.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
            aria-label="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={startManual}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Enter manually
          </button>
          <label className={`px-4 py-2 rounded-lg text-sm font-medium text-white cursor-pointer ${uploading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {uploading ? (
              <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Reading…</span>
            ) : (
              <span className="flex items-center gap-2"><Upload className="w-4 h-4" /> Upload prescription</span>
            )}
            <input
              type="file"
              className="hidden"
              accept="application/pdf,image/jpeg,image/png"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleUpload(file);
                e.target.value = '';
              }}
            />
          </label>
        </div>
      </div>

      {needsReviewCount > 0 && (
        <div className="mb-4 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {needsReviewCount} transfer{needsReviewCount === 1 ? '' : 's'} need{needsReviewCount === 1 ? 's' : ''} your review before {needsReviewCount === 1 ? 'it' : 'they'} can be ordered.
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}

      <div className="flex gap-2 mb-4">
        {(['open', 'all'] as const).map(value => (
          <button
            key={value}
            onClick={() => setFilter(value)}
            className={`px-3 py-1.5 rounded-lg text-sm ${filter === value ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            {value === 'open' ? 'Open' : 'All'}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : transfers.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <FileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="font-medium text-gray-700">No transferred prescriptions yet</p>
            <p className="text-sm mt-1">Upload a script from one of your doctors to get started.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">Patient</th>
                <th className="px-4 py-3 font-medium">Prescriber</th>
                <th className="px-4 py-3 font-medium">Medication</th>
                <th className="px-4 py-3 font-medium">Received</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {transfers.map(transfer => {
                const patient = transfer.parsed?.patient;
                const prescriber = transfer.parsed?.prescriber;
                return (
                  <tr
                    key={transfer._id}
                    onClick={() => setSelectedId(transfer._id)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {[patient?.firstName, patient?.lastName].filter(Boolean).join(' ') || <span className="text-gray-400">Not read</span>}
                      </div>
                      {patient?.dateOfBirth && (
                        <div className="text-xs text-gray-500">DOB {formatDate(patient.dateOfBirth)}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900">
                        {[prescriber?.firstName, prescriber?.lastName].filter(Boolean).join(' ') || <span className="text-gray-400">Not read</span>}
                      </div>
                      {prescriber?.npiNumber && <div className="text-xs text-gray-500">NPI {prescriber.npiNumber}</div>}
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      <div className="truncate text-gray-900">
                        {transfer.parsed?.medication?.description || <span className="text-gray-400">Not read</span>}
                      </div>
                      {transfer.duplicateOfId && (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-700 mt-0.5">
                          <Copy className="w-3 h-3" /> Possible resend
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(transfer.createdAt)}</td>
                    <td className="px-4 py-3"><TransferStatusBadge status={transfer.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {!loading && transfers.length >= TRANSFERS_LIMIT && (
          <div className="px-4 py-3 border-t border-gray-100 text-xs text-amber-800 bg-amber-50">
            Showing the newest {TRANSFERS_LIMIT} transfers. Older ones are not listed here.
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 mt-4 flex items-center gap-1">
        <ExternalLink className="w-3 h-3" />
        Scans and photos have no readable text, so those arrive blank for you to key in.
      </p>
    </div>
  );
};

export default PrescriptionTransfers;
